import React, { useState, useEffect } from 'react';
import {
  Activity,
  Bot,
  BrainCircuit,
  BarChart2,
  ShieldAlert,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { botInsightApi, decisionsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import type { TradingEvaluation } from '../types/titan';

interface BotSummary {
  pattern_id: string;
  pattern_name: string;
  is_active: boolean;
  trace_count: number;
  blocked_count: number;
  scanned_count: number;
  eval_count: number;
  match_count: number;
  signal_count: number;
  last_trace: string | null;
  latest_block_reason: string | null;
  latest_universe_size: number;
  match_rate: number;
  block_rate: number;
}

const FleetDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [bots, setBots] = useState<BotSummary[]>([]);
  const [evaluations, setEvaluations] = useState<TradingEvaluation[]>([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [insightRes, evalRes] = await Promise.all([
          botInsightApi.summary(24),
          decisionsApi.evaluations({ limit: 20 })
        ]);
        setBots(insightRes.bots || []);
        setEvaluations(evalRes.evaluations || []);
      } catch (error) {
        console.error('Error fetching fleet data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, []);

  const activeBots = bots.filter((b) => b.is_active);
  const totalScans = bots.reduce((sum, b) => sum + b.scanned_count, 0);
  const totalMatches = bots.reduce((sum, b) => sum + b.match_count, 0);
  const totalSignals = bots.reduce((sum, b) => sum + b.signal_count, 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10 animate-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-foreground flex items-center gap-2">
            <Bot className="w-8 h-8 text-primary" />
            Fleet Observability
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time insights into AI fleet selections, strategies, and autonomous decision-making.
          </p>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card px-3 py-1.5 rounded-full border">
            <Activity className="w-4 h-4 animate-spin text-primary" />
            Syncing Telemetry...
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Active Fleet</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              {activeBots.length}
              <span className="text-sm font-normal text-muted-foreground">/ {bots.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Bots deployed by autonomous agents.</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Total Scans (24h)</CardDescription>
            <CardTitle className="text-3xl">{totalScans.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Assets evaluated across all active fleets.</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Pattern Matches</CardDescription>
            <CardTitle className="text-3xl text-emerald-500">{totalMatches.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Technical setups identified.</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-sm">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Trade Signals</CardDescription>
            <CardTitle className="text-3xl text-indigo-500">{totalSignals.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Signals surviving all AI validations.</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="w-full justify-start border-b rounded-none pb-px h-auto bg-transparent p-0 space-x-6">
          <TabsTrigger value="metrics" className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent rounded-none px-0 py-3 data-[state=active]:text-foreground text-muted-foreground font-medium">
            <BarChart2 className="w-4 h-4 mr-2" />
            Fleet Metrics
          </TabsTrigger>
          <TabsTrigger value="decisions" className="data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent rounded-none px-0 py-3 data-[state=active]:text-foreground text-muted-foreground font-medium">
            <BrainCircuit className="w-4 h-4 mr-2" />
            Decision Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="decisions" className="pt-6 mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Recent Evaluations</CardTitle>
              <CardDescription>Real-time AI logic and rationale for signal generation.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead>Fleet Member</TableHead>
                      <TableHead>Decision</TableHead>
                      <TableHead>AI Rationale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evaluations.map((ev, i) => (
                      <TableRow key={ev.id || i}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(ev.timestamp).toLocaleTimeString()}
                        </TableCell>
                        <TableCell className="font-bold">{ev.symbol}</TableCell>
                        <TableCell className="text-sm">{ev.pattern_name}</TableCell>
                        <TableCell>
                          {ev.matched ? (
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Matched</Badge>
                          ) : (
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate" title={ev.reasoning || ''}>
                          {ev.reasoning || 'No rationale provided'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {evaluations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                          No recent evaluations found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="pt-6 mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Fleet Telemetry</CardTitle>
              <CardDescription>Detailed scan and signal metrics per active bot (24h window).</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bot Strategy</TableHead>
                      <TableHead className="text-right">Universe Size</TableHead>
                      <TableHead className="text-right">Assets Scanned</TableHead>
                      <TableHead className="text-right">Matches</TableHead>
                      <TableHead className="text-right">Match Rate</TableHead>
                      <TableHead className="text-right">Signals Fired</TableHead>
                      <TableHead>Health</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeBots.map((bot) => (
                      <TableRow key={bot.pattern_id}>
                        <TableCell className="font-medium">{bot.pattern_name}</TableCell>
                        <TableCell className="text-right">{bot.latest_universe_size}</TableCell>
                        <TableCell className="text-right">{bot.scanned_count.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-emerald-500 font-medium">{bot.match_count.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{(bot.match_rate * 100).toFixed(1)}%</TableCell>
                        <TableCell className="text-right font-bold text-indigo-400">{bot.signal_count.toLocaleString()}</TableCell>
                        <TableCell>
                          {bot.latest_block_reason ? (
                            <Badge variant="outline" className="text-amber-500 border-amber-500/30 flex items-center gap-1 w-fit">
                              <ShieldAlert className="w-3 h-3" /> Blocked: {bot.latest_block_reason}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-500 border-emerald-500/30 w-fit">
                              Healthy
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {activeBots.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                          No active bots in the fleet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FleetDashboard;
